/**
 * Founder Data Room. Shows an overview (progress ring, category cards, EIR
 * notify, bulk download) or a single category's checklist of upload items,
 * switched by the :categoryId route param.
 *
 * Polls getDataRoom every 30s to stay fresh and mirrors categories into
 * DataRoomContext for the sidebar. Optimistic upload state is applied locally,
 * then reconciled with a server re-fetch because presigned file URLs from the
 * upload response can differ from those returned by a fresh GET.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getDataRoom, uploadDataRoomItem, notifyEIROfUpload, exportDataRoom, downloadBlankTemplate } from '../services/apiService';
import { useDataRoom } from '../context/DataRoomContext';
import CircularProgress from '../components/CircularProgress';
import CategoryCard, { getUrgency } from '../components/CategoryCard';
import ItemRow from '../components/ItemRow';
import UploadModal from '../components/UploadModal';
import BulkUploadModal from '../components/BulkUploadModal';
import FounderNotesWidget from '../components/FounderNotesWidget';

/** Founder data-room view; renders overview or a category checklist by route. */
export default function DataRoom() {
  const { categoryId } = useParams();
  const activeCatId = categoryId ? parseInt(categoryId, 10) : null;

  const { setCategories } = useDataRoom();

  const [dataRoom, setDataRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [bulkTarget, setBulkTarget]     = useState(null);

  function loadDataRoom() {
    setLoading(true);
    setLoadError(null);
    getDataRoom()
      .then((data) => { setDataRoom(data); setCategories(data.categories); })
      .catch(() => setLoadError('Unable to load your data room. Please refresh the page.'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadDataRoom();
    const interval = setInterval(() => {
      getDataRoom()
        .then((data) => { setDataRoom(data); setCategories(data.categories); })
        .catch(() => {});
    }, 30_000);
    return () => { clearInterval(interval); setCategories([]); };
  }, []); // intentionally run once on mount

  // Shared: applies a completed file upload to data room state without closing any modal.
  async function uploadItem(itemId, file) {
    const fileRecord = await uploadDataRoomItem(itemId, file);
    setDataRoom((prev) => {
      const categories = prev.categories.map((cat) => ({
        ...cat,
        items: cat.items.map((item) => {
          if (item.id !== itemId) return item;
          const prevFile = item.currentFile;
          const newVersions = prevFile
            ? [...item.versions.filter((v) => v.url !== prevFile.url), prevFile]
            : [...item.versions];
          return {
            ...item,
            status: 'uploaded',
            currentFile: fileRecord,
            versions: newVersions,
          };
        }),
      }));
      // Mirror the backend's authoritative rule: EIR sign-off wins, else upload.
      const isDone = (i) => {
        if (i.review?.status === 'complete') return true;
        if (i.review?.status === 'needs_work') return false;
        return i.status === 'complete' || i.status === 'uploaded';
      };
      const updatedCategories = categories.map((cat) => ({
        ...cat,
        completedItems: cat.items.filter((i) => !i.optional && isDone(i)).length,
      }));
      const total = updatedCategories.reduce((s, c) => s + c.totalItems, 0);
      const completed = updatedCategories.reduce((s, c) => s + c.completedItems, 0);
      return {
        ...prev,
        categories: updatedCategories,
        completedItems: completed,
        overallProgress: total > 0 ? Math.round((completed / total) * 100) : 0,
      };
    });
  }

  async function handleUpload(itemId, file) {
    await uploadItem(itemId, file);
    setUploadTarget(null);
    // Refresh from server so currentFile URLs are authoritative (presigned URLs
    // returned by the upload response can differ from a fresh GET).
    getDataRoom()
      .then((data) => { setDataRoom(data); setCategories(data.categories); })
      .catch(() => {});
  }

  if (loading) return <div style={styles.loading}>Loading...</div>;

  if (loadError) {
    return (
      <div style={styles.loading}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ marginBottom: 16 }}>{loadError}</p>
          <button style={styles.retryBtn} onClick={loadDataRoom}>Try again</button>
        </div>
      </div>
    );
  }

  const activeCategory = activeCatId
    ? dataRoom.categories.find((c) => c.id === activeCatId)
    : null;

  return (
    <div style={styles.layout}>
      <main style={styles.main}>
        {!activeCategory ? (
          <OverviewView dataRoom={dataRoom} />
        ) : (
          <CategoryView
            category={activeCategory}
            onUpload={(item) => setUploadTarget(item)}
            onBulkUpload={() => setBulkTarget(activeCategory)}
          />
        )}
      </main>

      {uploadTarget && (
        <UploadModal
          item={uploadTarget}
          onUpload={handleUpload}
          onClose={() => setUploadTarget(null)}
        />
      )}
      {bulkTarget && (
        <BulkUploadModal
          category={bulkTarget}
          onUploadItem={uploadItem}
          onClose={() => setBulkTarget(null)}
        />
      )}
      <FounderNotesWidget />
    </div>
  );
}

// Overview tab: progress ring, status summary, category grid, and the bulk
// export / "notify my EIR" actions. Both network actions fail silently — a
// failed notify is non-critical since the EIR can still see uploads directly.
function OverviewView({ dataRoom }) {
  const [notifyConfirm, setNotifyConfirm] = useState(false);
  const [notifyChecked, setNotifyChecked] = useState(true);
  const [sending, setSending]             = useState(false);
  const [notifySent, setNotifySent]       = useState(false);
  const [downloading, setDownloading]     = useState(false);
  const [templating, setTemplating]       = useState(false);

  async function handleNotify() {
    if (!notifyChecked) { setNotifyConfirm(false); return; }
    setSending(true);
    try {
      await notifyEIROfUpload();
      setNotifySent(true);
      setNotifyConfirm(false);
    } catch {
      // fail silently — EIR will still see the uploads
    } finally {
      setSending(false);
    }
  }

  async function handleExport() {
    setDownloading(true);
    try {
      const blob = await exportDataRoom();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Data Room.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // silent
    } finally {
      setDownloading(false);
    }
  }

  async function handleTemplateDownload() {
    setTemplating(true);
    try {
      const blob = await downloadBlankTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Data Room Template.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // silent
    } finally {
      setTemplating(false);
    }
  }

  return (
    <div style={styles.overviewWrapper} className="page-scroll">
      <h1 style={styles.pageTitle}>Data Room</h1>

      <div style={styles.progressCenter}>
        <CircularProgress
          percent={dataRoom.overallProgress}
          nextDueDate={dataRoom.nextDueDate}
          completedItems={dataRoom.completedItems}
          totalItems={dataRoom.totalItems}
          size={160}
          stroke={14}
        />
      </div>

      {/* Action buttons row: Bulk Download + Notify EIR */}
      <div style={styles.notifySection}>
        <div style={styles.actionRow}>
          <button style={styles.actionBtn} onClick={handleExport} disabled={downloading}>
            {downloading ? 'Preparing…' : 'Bulk Download'}
          </button>
          <button style={styles.actionBtn} onClick={handleTemplateDownload} disabled={templating}>
            {templating ? 'Preparing…' : 'Download Blank Template'}
          </button>
          {dataRoom.assignedEirName && (
            <>
              {notifySent ? (
                <p style={styles.notifySent}>✓ {dataRoom.assignedEirName} has been notified.</p>
              ) : !notifyConfirm ? (
                <button style={styles.actionBtn} onClick={() => setNotifyConfirm(true)}>
                  Notify my EIR
                </button>
              ) : (
                <div style={styles.notifyConfirmRow}>
                  <label style={styles.notifyCheckLabel}>
                    <input
                      type="checkbox"
                      checked={notifyChecked}
                      onChange={(e) => setNotifyChecked(e.target.checked)}
                      style={{ accentColor: 'var(--color-forest)', marginRight: 6 }}
                    />
                    Send email to {dataRoom.assignedEirName}
                  </label>
                  <div style={styles.notifyBtnRow}>
                    <button style={styles.notifyConfirmBtn} onClick={handleNotify} disabled={sending}>
                      {sending ? 'Sending…' : 'Confirm'}
                    </button>
                    <button style={styles.notifyCancelBtn} onClick={() => setNotifyConfirm(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <StatusSummary categories={dataRoom.categories} />

      <div style={styles.cardList}>
        {dataRoom.categories.map((cat) => (
          <CategoryCard key={cat.id} category={cat} />
        ))}
      </div>
    </div>
  );
}

// Rolls per-category urgency into overdue/coming-up/on-track/complete counts
// and renders one chip per non-empty bucket.
function StatusSummary({ categories }) {
  const counts = { overdue: 0, soon: 0, ok: 0, complete: 0 };
  categories.forEach((cat) => {
    const { status } = getUrgency(cat.dueDate, cat.completedItems, cat.totalItems);
    if (status === 'complete') counts.complete++;
    else if (status === 'overdue' || status === 'today') counts.overdue++;
    else if (status === 'urgent' || status === 'soon') counts.soon++;
    else counts.ok++;
  });

  const chips = [
    counts.overdue > 0 && { label: `${counts.overdue} overdue`,    bg: '#fee2e2', color: '#dc2626' },
    counts.soon    > 0 && { label: `${counts.soon} coming up`,      bg: '#fef3c7', color: '#d97706' },
    counts.ok      > 0 && { label: `${counts.ok} on track`,         bg: '#f0fdf4', color: '#15803d' },
    counts.complete > 0 && { label: `${counts.complete} complete`,  bg: '#dcfce7', color: '#15803d' },
  ].filter(Boolean);

  if (chips.length === 0) return null;

  return (
    <div style={styles.summaryRow}>
      {chips.map((c) => (
        <span key={c.label} style={{ ...styles.summaryChip, background: c.bg, color: c.color }}>
          {c.label}
        </span>
      ))}
    </div>
  );
}

function CategoryView({ category, onUpload, onBulkUpload }) {
  const [showTemplates, setShowTemplates] = useState(false);

  return (
    <div style={styles.categoryWrapper}>
      <div style={styles.categoryHeader}>
        <h1 style={{ ...styles.pageTitle, textAlign: 'left' }}>{category.id}. {category.name}</h1>
        <div style={styles.categoryMeta}>
          <div style={styles.metaProgressBar}>
            <div
              style={{
                ...styles.metaProgressFill,
                width: `${Math.round((category.completedItems / category.totalItems) * 100)}%`,
              }}
            />
          </div>
          <span style={styles.metaText}>
            {category.completedItems} of {category.totalItems} completed
          </span>
          <span style={styles.metaDue}>Due: {category.dueDate}</span>
          <button style={{ ...styles.actionBtn, marginLeft: 'auto' }} onClick={() => setShowTemplates(true)}>
            Templates
          </button>
          <button style={styles.actionBtn} onClick={onBulkUpload}>
            Bulk Upload
          </button>
        </div>
      </div>

      <div style={styles.itemList}>
        {category.items.map((item) => (
          <ItemRow key={item.id} item={item} onUpload={onUpload} />
        ))}
      </div>

      {showTemplates && <TemplateModal onClose={() => setShowTemplates(false)} />}
    </div>
  );
}

const TEMPLATES = [
  {
    label: 'Cap Table',
    filename: 'Cap Table template.xlsx',
    description: 'Tracks every owner in your company — founders, investors, advisors, and option holders — with their share count, ownership percentage, and fully-diluted position. Fill in your current membership interests and any outstanding agreements to issue equity.',
  },
  {
    label: 'SaaS',
    filename: 'SaaS Pro Forma.xlsx',
    description: 'Built for subscription businesses. Models monthly recurring revenue, new subscriber growth, churn, customer acquisition cost, and lifetime value across a 3–5 year horizon. Enter your pricing tiers, current subscriber count, and growth assumptions.',
  },
  {
    label: 'Services',
    filename: 'Services Pro Forma.xlsx',
    description: 'Designed for professional and consulting services firms. Projects revenue by engagement type, billable hours, utilization rate, and headcount. Enter your service lines, average engagement value, and hiring plan.',
  },
  {
    label: 'Product',
    filename: 'Product Pro Forma.xlsx',
    description: 'For businesses selling physical or digital products. Models units sold, average selling price, cost of goods sold, gross margin, and inventory needs. Enter your product lines, unit economics, and sales channel assumptions.',
  },
  {
    label: 'Marketplace',
    filename: 'Marketplace Pro Forma.xlsx',
    description: 'Covers two-sided marketplace dynamics — gross merchandise value, take rate, buyer and seller growth, and transaction volume. Enter your current GMV, commission structure, and supply/demand growth rates.',
  },
  {
    label: 'Manufacturing',
    filename: 'Manufacturing Pro Forma.xlsx',
    description: 'For product manufacturers. Projects production volume, per-unit manufacturing cost, capacity utilization, capex requirements, and working capital needs. Enter your production capacity, bill of materials, and demand forecast.',
  },
];

async function downloadTemplate(filename) {
  try {
    const resp = await fetch(`/api/vr/templates/${encodeURIComponent(filename)}`, { credentials: 'include' });
    if (!resp.ok) return;
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {
    // silent
  }
}

// Financial-template browser. Previews the first ~40 rows of the active .xlsx
// using the globally-loaded window.XLSX (preview is skipped if it's absent).
// The cancelled flag guards against state updates after the tab switches.
function TemplateModal({ onClose }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [rows, setRows] = useState([]);
  const [sheetLoading, setSheetLoading] = useState(true);
  const active = TEMPLATES[activeIdx];

  useEffect(() => {
    let cancelled = false;
    setSheetLoading(true);
    setRows([]);

    const XLSX = window.XLSX;
    if (!XLSX) {
      setRows([]);
      setSheetLoading(false);
      return () => { cancelled = true; };
    }

    fetch(`/api/vr/templates/${encodeURIComponent(active.filename)}`, { credentials: 'include' })
      .then((r) => r.arrayBuffer())
      .then((buf) => {
        if (cancelled) return;
        const wb = XLSX.read(buf, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        setRows(data.slice(0, 40));
      })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setSheetLoading(false); });

    return () => { cancelled = true; };
  }, [activeIdx]);

  return (
    <div style={tmStyles.backdrop} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={tmStyles.modal}>
        <div style={tmStyles.header}>
          <h2 style={tmStyles.title}>Financial Templates</h2>
          <button style={tmStyles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={tmStyles.tabs}>
          {TEMPLATES.map((t, i) => (
            <button
              key={t.label}
              style={{ ...tmStyles.tab, ...(activeIdx === i ? tmStyles.tabActive : {}) }}
              onClick={() => setActiveIdx(i)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <p style={tmStyles.description}>{active.description}</p>

        <div style={tmStyles.sheetWrap}>
          {sheetLoading && <p style={tmStyles.sheetMsg}>Loading preview…</p>}
          {!sheetLoading && rows.length === 0 && <p style={tmStyles.sheetMsg}>Preview unavailable.</p>}
          {!sheetLoading && rows.length > 0 && (
            <table style={tmStyles.table}>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri} style={ri === 0 ? tmStyles.headerRow : {}}>
                    {row.map((cell, ci) => {
                      const Tag = ri === 0 ? 'th' : 'td';
                      return <Tag key={ci} style={ri === 0 ? tmStyles.th : tmStyles.td}>{cell}</Tag>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={tmStyles.actions}>
          <button
            style={tmStyles.downloadBtn}
            onClick={() => downloadTemplate(active.filename)}
          >
            Download Template
          </button>
          <button style={tmStyles.cancelBtn} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  layout: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    background: 'linear-gradient(to bottom, var(--color-frost) 0%, var(--color-mint-dark) 100%) fixed',
  },
  main: {
    flex: 1,
    overflowY: 'auto',
    padding: 0,
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    fontFamily: 'var(--font-body)',
    fontSize: 16,
    color: 'var(--color-forest)',
  },
  retryBtn: {
    padding: '8px 20px',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--color-forest)',
    color: '#fff',
    fontFamily: 'var(--font-body)',
    fontSize: 15,
    fontWeight: 800,
    border: 'none',
    cursor: 'pointer',
  },

  /* Overview */
  overviewWrapper: {
    padding: 'var(--space-8) var(--space-6)',
    maxWidth: '98%',
    margin: '0 auto',
  },
  pageTitle: {
    fontFamily: 'var(--font-accent)',
    fontSize: 'var(--font-size-page-title)',
    fontWeight: 'var(--font-weight-page-title)',
    color: 'var(--color-rust)',
    lineHeight: 1.1,
    textAlign: 'center',
    marginBottom: 'var(--space-6)',
  },
  progressCenter: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 'var(--space-8)',
  },
  summaryRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginBottom: 'var(--space-6)',
  },
  summaryChip: {
    padding: '4px 12px',
    borderRadius: 'var(--radius-full)',
    fontSize: 16,
    fontWeight: 800,
    letterSpacing: '0.03em',
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
  },

  /* Category detail */
  categoryWrapper: {
    padding: 'var(--space-6) var(--space-8)',
  },
  categoryHeader: {
    marginBottom: 'var(--space-6)',
    paddingBottom: 'var(--space-4)',
    borderBottom: '1.5px solid var(--color-mint-dark)',
  },
  actionBtn: {
    display: 'flex', alignItems: 'center',
    fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700,
    color: 'var(--color-forest)', background: '#fff',
    border: '1.5px solid var(--color-mint-dark)',
    borderRadius: 'var(--radius-sm)', padding: '7px 16px',
    cursor: 'pointer', flexShrink: 0,
  },
  categoryMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-4)',
    marginTop: 'var(--space-3)',
    flexWrap: 'wrap',
  },
  metaProgressBar: {
    flex: 1,
    maxWidth: 240,
    height: 8,
    background: 'var(--color-mint-dark)',
    borderRadius: 'var(--radius-full)',
    overflow: 'hidden',
  },
  metaProgressFill: {
    height: '100%',
    background: 'var(--color-forest)',
    borderRadius: 'var(--radius-full)',
    transition: 'width 0.4s ease',
  },
  metaText: {
    fontFamily: 'var(--font-body)',
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--color-forest)',
  },
  metaDue: {
    fontFamily: 'var(--font-body)',
    fontSize: 16,
    fontWeight: 700,
    color: 'var(--color-forest)',
    opacity: 0.7,
  },
  itemList: {
    display: 'flex',
    flexDirection: 'column',
  },
  notifySection:      { marginBottom: 'var(--space-4)' },
  actionRow:          { display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' },
  notifySent:         { fontFamily: 'var(--font-body)', fontSize: 13, color: '#15803d', fontWeight: 600 },
  notifyConfirmRow:   { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 },
  notifyCheckLabel:   { display: 'flex', alignItems: 'center', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', cursor: 'pointer' },
  notifyBtnRow:       { display: 'flex', gap: 8 },
  notifyConfirmBtn:   { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, color: '#fff', background: 'var(--color-forest)', border: 'none', borderRadius: 'var(--radius-sm)', padding: '7px 16px', cursor: 'pointer' },
  notifyCancelBtn:    { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--color-forest)', background: 'var(--color-mint)', border: '1.5px solid var(--color-mint-dark)', borderRadius: 'var(--radius-sm)', padding: '7px 14px', cursor: 'pointer' },
};

const tmStyles = {
  backdrop:    { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal:       { background: '#fff', borderRadius: 'var(--radius-lg)', boxShadow: '0 8px 40px rgba(33,81,45,0.18)', width: 820, maxWidth: '96vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  header:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--color-mint-dark)', background: 'var(--color-frost)', flexShrink: 0 },
  title:       { fontFamily: 'var(--font-body)', fontSize: 17, fontWeight: 700, color: 'var(--color-forest)' },
  closeBtn:    { background: 'none', border: 'none', fontSize: 18, color: 'var(--color-forest)', opacity: 0.5, cursor: 'pointer', lineHeight: 1 },
  tabs:        { display: 'flex', gap: 6, padding: '14px 24px 0', flexWrap: 'wrap', flexShrink: 0 },
  tab:         { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, padding: '5px 14px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-mint-dark)', background: 'var(--color-frost)', color: 'var(--color-forest)', cursor: 'pointer' },
  tabActive:   { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, padding: '5px 14px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-forest)', background: 'var(--color-forest)', color: '#fff', cursor: 'pointer' },
  description: { fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-forest)', lineHeight: 1.65, padding: '12px 24px 0', opacity: 0.8, flexShrink: 0 },
  sheetWrap:   { flex: 1, overflow: 'auto', margin: '12px 24px', border: '1px solid var(--color-mint-dark)', borderRadius: 'var(--radius-sm)', background: '#fafafa' },
  sheetMsg:    { fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-forest)', opacity: 0.45, padding: 24, textAlign: 'center', fontStyle: 'italic' },
  table:       { borderCollapse: 'collapse', fontSize: 12, fontFamily: 'var(--font-body)', width: '100%' },
  headerRow:   { background: 'var(--color-forest)' },
  th:          { padding: '6px 10px', fontWeight: 700, color: '#fff', textAlign: 'left', whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.15)' },
  td:          { padding: '5px 10px', color: 'var(--color-forest)', borderBottom: '1px solid #eef0ee', borderRight: '1px solid #eef0ee', whiteSpace: 'nowrap' },
  actions:     { display: 'flex', gap: 10, padding: '14px 24px', borderTop: '1px solid var(--color-mint-dark)', background: 'var(--color-frost)', flexShrink: 0 },
  downloadBtn: { display: 'inline-flex', alignItems: 'center', padding: '8px 20px', borderRadius: 'var(--radius-sm)', background: 'var(--color-forest)', color: '#fff', fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 700, textDecoration: 'none', cursor: 'pointer' },
  cancelBtn:   { padding: '8px 16px', borderRadius: 'var(--radius-sm)', background: 'none', border: '1.5px solid var(--color-mint-dark)', color: 'var(--color-forest)', fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};
